import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, RouterOutlet } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { MenuComponent } from './components/menu/menu.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { RefreshButtonComponent } from './components/refresh-button/refresh-button.component';
import { ServerLinkComponent } from './components/server-link/server-link.component';
import { TogglerComponent } from './components/toggler/toggler.component';
import { TrafikLeftComponent } from './components/trafik-left/trafik-left.component';
import { TrafikPanelComponent } from './components/trafik-panel/trafik-panel.component';
import { ProgressBarComponent } from './components/ui/progress-bar/progress-bar.component';
import { IconsModule } from './icons/icons.module';
import { ConnectionHeaderComponent } from './pages/home/connection-header/connection-header.component';
import { HomeComponent } from './pages/home/home.component';
import { RuleManagementComponent } from './pages/rules-management/rule-management.component';
import { InputComponent } from './components/ui/input/input.component';
import { InfoBlockComponent } from './components/info-block/info-block.component';
import { AddedUsersListComponent } from './components/added-users-list/added-users-list.component';
import { UserRowComponent } from './components/user-row/user-row.component';
import { AddButtonComponent } from './components/ui/add-button/add-button.component';
import { BuildInComponent } from './components/build-in/build-in.component';
import { ServerRowComponent } from './components/server-row/server-row.component';
import { ChipsComponent } from './components/chips/chips.component';
import { ButtonComponent } from './components/ui/button/button.component';
import { TransparentButtonComponent } from './components/ui/transparent-button/transparent-button.component';
import { SubscriptionComponent } from './pages/subscription/subscription.component';
import { PaymentBlockComponent } from './components/payment-block/payment-block.component';
import { TarifBlockComponent } from './components/tarif-block/tarif-block.component';
import { RechargeRecordComponent } from './components/recharge-record/recharge-record.component';
import { RecordComponent } from './components/record/record.component';
import { DevicesComponent } from './pages/devices/devices.component';
import { DeviceListComponent } from './components/device-list/device-list.component';
import { ConnectedDevicesComponent } from './components/connected-devices/connected-devices.component';
import { AuthComponent } from './pages/auth/auth.component';
import { RegistrationFormComponent } from './components/registration-form/registration-form.component';
import { PasswordInputComponent } from './components/ui/password-input/password-input.component';
import { ServersComponent } from './components/servers/servers.component';
import { ServersPage } from './pages/servers/servers.component';
import { WebUiComponent } from './web-ui.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { UserTabsComponent } from './components/user-tabs.component';
import { SigninFormComponent } from './components/signin-form/signin-form.component';
import { SignupFormComponent } from './components/signup-form/signup-form.component';
import { ClientInstallationComponent } from './components/client-installation/client-installation.component';
import { ThirdPartyClientComponent } from './components/third-party-client/third-party-client.component';
import { ToastComponent } from './components/ui/toast/toast.component';
import { ToastService } from './components/ui/toast/toast.service';
import { ModalComponent } from './components/ui/modal/modal.component';
import { RechargeComponent } from './pages/recharge/recharge.component';
import { HeaderComponent } from './components/header/header.component';
import { ChatwootComponent } from './components/chatwoot/chatwoot.component';
import { WebLayoutComponent } from './layouts/web-layout/web-layout.component';
import { ExtensionLayoutComponent } from './layouts/extension-layout/extension-layout.component';
import { SettingsModule } from './pages/settings/settings.module';
import { InvoiceStatus } from './components/invocie-status/invoice-status.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './lib/assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    WebUiComponent,
    HomeComponent,
    RuleManagementComponent,
    MenuComponent,
    SidebarComponent,
    ConnectionHeaderComponent,
    TrafikPanelComponent,
    ProgressBarComponent,
    TogglerComponent,
    ServerLinkComponent,
    TrafikLeftComponent,
    RefreshButtonComponent,
    InputComponent,
    InfoBlockComponent,
    AddedUsersListComponent,
    UserRowComponent,
    AddButtonComponent,
    BuildInComponent,
    ServerRowComponent,
    ModalComponent,
    ChipsComponent,
    ButtonComponent,
    TransparentButtonComponent,
    SubscriptionComponent,
    TarifBlockComponent,
    PaymentBlockComponent,
    RechargeRecordComponent,
    RechargeComponent,
    RecordComponent,
    DevicesComponent,
    DeviceListComponent,
    ConnectedDevicesComponent,
    AuthComponent,
    RegistrationFormComponent,
    PasswordInputComponent,
    ServersComponent,
    DashboardComponent,
    UserTabsComponent,
    SigninFormComponent,
    SignupFormComponent,
    ForgotPasswordComponent,
    ClientInstallationComponent,
    ThirdPartyClientComponent,
    ToastComponent,
    ModalComponent,
    HeaderComponent,
    WebLayoutComponent,
    ExtensionLayoutComponent,
    ChatwootComponent,
    InvoiceStatus,
    ServersPage,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    RouterModule,
    RouterOutlet,
    BrowserAnimationsModule,
    IconsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en-US',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    FormsModule,
    ReactiveFormsModule,
    SettingsModule
  ],
  exports: [
    TrafikPanelComponent,
    TrafikLeftComponent,
    InputComponent,
    WebUiComponent,
    HomeComponent,
    RuleManagementComponent,
    MenuComponent,
    SidebarComponent,
    ConnectionHeaderComponent,
    TrafikPanelComponent,
    ProgressBarComponent,
    TogglerComponent,
    ServerLinkComponent,
    TrafikLeftComponent,
    RefreshButtonComponent,
    InputComponent,
    InfoBlockComponent,
    AddedUsersListComponent,
    UserRowComponent,
    AddButtonComponent,
    BuildInComponent,
    ServerRowComponent,
    ModalComponent,
    ChipsComponent,
    ButtonComponent,
    TransparentButtonComponent,
    SubscriptionComponent,
    TarifBlockComponent,
    PaymentBlockComponent,
    RechargeRecordComponent,
    RechargeComponent,
    RecordComponent,
    DevicesComponent,
    DeviceListComponent,
    ConnectedDevicesComponent,
    AuthComponent,
    RegistrationFormComponent,
    PasswordInputComponent,
    ServersComponent,
    DashboardComponent,
    ClientInstallationComponent,
    ThirdPartyClientComponent,
    ToastComponent,
    ModalComponent,
    WebLayoutComponent,
    ExtensionLayoutComponent,
    ChatwootComponent,
    InvoiceStatus,
    ForgotPasswordComponent,
    ServersPage,
  ],
  providers: [
    ToastService,
    // 动态提供LOCALE_ID，使其能够根据用户选择的语言更新
    {
      provide: LOCALE_ID,
      useFactory: () => localStorage.getItem('selectedLanguage') || 'zh-CN'
    }
  ],
  bootstrap: [WebUiComponent],
})
export class WebUiModule { }
